import React, { useState, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { Button, Badge, Alert, OverlayTrigger, Tooltip } from 'react-bootstrap';
import CustomTable from '../../components/CustomTable/CustomTable';
import ComebackArrow from '../../components/ComebackArrow/ComebackArrow';
import useMediaQuery from '../../hooks/useMediaQuery';
import { format, parseISO, isSameDay } from 'date-fns';
import { it } from 'date-fns/locale';
import DatePicker from 'react-datepicker';
import "react-datepicker/dist/react-datepicker.css";
import useLezioni from '../../hooks/useLezioni';
import usePresenze from '../../hooks/usePresenze';
import useClassiUtenti from '../../hooks/useClassiUtenti';
import LoadingTable from '../../components/LoadingTable/LoadingTable';

export default function PresenzePage() {
  const {getLezioniByIdClasse} = useLezioni();
  const {
    getRegistroPresenzeByParams,
    createRegistroPresenze,
    updateRegistroPresenze,
    loadingStatePresenze
  } = usePresenze();
  const {getClassiUtentiByIdClasse} = useClassiUtenti();
  const location = useLocation();
  const navigate = useNavigate();
  const classeData = location.state?.classeData;
  const isWideScreen = useMediaQuery('(min-width: 1200px)');
  const isSmallScreen = useMediaQuery('(max-width: 767px)');

  const [attendanceData, setAttendanceData] = useState([]);
  const [classiUtenti, setClassiUtenti] = useState([]);
  const [lezioni, setLezioni] = useState([]);
  const [selectedDate, setSelectedDate] = useState(null);
  const [selectedLezione, setSelectedLezione] = useState(null);
  const [isInitialLoading, setIsInitialLoading] = useState(true);
  const [noDataMessage, setNoDataMessage] = useState('');

  useEffect(() => {
    if (!classeData?.corso?.id_corso) {
      navigate("/gestione-classi");
      return;
    }
    initialFetch();
  }, [classeData, navigate]);

  const initialFetch = async () => {
    setIsInitialLoading(true);
    try {
      const [lezioniData, classiUtentiData] = await Promise.all([
        getLezioniByIdClasse({ id_classe: classeData?.id_classe }),
        getClassiUtentiByIdClasse({ id_classe: classeData?.id_classe })
      ]);

      setLezioni(lezioniData);
      const corsisti = classiUtentiData?.filter(utente => utente.tipo_utente === "corsista");
      setClassiUtenti(corsisti);

      if (lezioniData?.length === 0 && corsisti.length === 0) {
        setNoDataMessage('Non ci sono lezioni né utenti registrati per questa classe.');
      } else if (lezioniData?.length === 0) {
        setNoDataMessage('Non ci sono lezioni registrate per questa classe.');
      } else if (corsisti.length === 0) {
        setNoDataMessage('Non ci sono utenti registrati per questa classe.');
      } else {
        setNoDataMessage('');
        const primaLezione = lezioniData[0];
        setSelectedDate(parseISO(primaLezione.data));
        setSelectedLezione(primaLezione);
      }
    } catch (error) {
      console.error("Error in initial fetch:", error);
      setNoDataMessage('Si è verificato un errore nel caricamento dei dati.');
    } finally {
      setIsInitialLoading(false);
    }
  };

  useEffect(() => {
    if (selectedLezione && classiUtenti.length > 0) {
      fetchPresenze(selectedLezione.id_lezione);
    }
  }, [selectedLezione?.id_lezione, classiUtenti]);

  const fetchPresenze = async (lezioneId) => {
      const data = { id_lezione: lezioneId };
      const presenzeData = await getRegistroPresenzeByParams(data);
      
      const presenze = classiUtenti.map(utente => {
        const presenza = presenzeData.data.find(p => p.alunno.id_utente === utente.id_utente && p.id_lezione === lezioneId);
        return {
          id_presenza: presenza ? presenza.id_presenza : null,
          id_lezione: lezioneId,
          alunno: {
            id_utente: utente.id_utente,
            nome: utente.nome,
            cognome: utente.cognome,
            email: utente.username
          },
          presente: presenza ? Boolean(presenza.presente) : null,
          segnata_da: presenza?.segnata_da || null,
          data_modifica: presenza?.data_modifica || null
        };
      });

      setAttendanceData(presenze);
   
  };

  const handleDateChange = (date) => {
    setSelectedDate(date);
    const lezione = lezioni.find(l => isSameDay(parseISO(l.data), date));
    if (lezione) {
      setSelectedLezione(lezione);
    }
  };

  const handlePrevNextLezione = (direction) => {
    const currentIndex = lezioni.findIndex(l => l.id_lezione === selectedLezione.id_lezione);
    const newIndex = direction === 'prev' ? currentIndex - 1 : currentIndex + 1;
    if (newIndex >= 0 && newIndex < lezioni?.length) {
      const newLezione = lezioni[newIndex];
      setSelectedDate(parseISO(newLezione.data));
      setSelectedLezione(newLezione);
    }
  };

  const columns = [
    { 
      key: 'alunno.nome', 
      label: 'Nome', 
      sortable: true,
      formatter: (nome, row) => `${nome || ''} ${row.alunno?.cognome || ''}`.trim() || 'N/A'
    },
    { key: 'alunno.email', label: 'Email', sortable: true },
    { 
      key: 'presente', 
      label: 'Presenza', 
      sortable: true,
      formatter: (value, row) => {
        if (value === null) return <Badge bg="dark">Non inserito</Badge>;

        const tooltipContent = row.segnata_da ? (
          <div style={{ textAlign: 'left' }}>
            <div><strong>Segnato da:</strong></div>
            <div style={{ marginBottom: '8px' }}>
              {row.segnata_da.nome} {row.segnata_da.cognome}{" "}
              <small style={{ color: '#666' }}>({row.segnata_da.tipo_utente?.replace('_', ' ')})</small>
            </div>
            {row.data_modifica && (
              <>
                <div><strong>Data:</strong></div>
                <div>{format(parseISO(row.data_modifica), 'dd/MM/yyyy HH:mm')}</div>
              </>
            )}
          </div>
        ) : 'Nessuna informazione disponibile';

        return (
          <>
            <Badge bg={value ? 'success' : 'danger'}>
              {value ? 'Presente' : 'Assente'}
            </Badge>
            <OverlayTrigger
              placement="top"
              overlay={
                <Tooltip className="custom-presence-tooltip">
                  {tooltipContent}
                </Tooltip>
              }
            >
              <i className="bi bi-info-circle ms-2" style={{cursor: 'help', color: '#6c757d'}}></i>
            </OverlayTrigger>
          </>
        );
      }
    },
  ];
 
  const handlePresenceChange = async (userId, isPresent) => {
    if (!selectedLezione) return;

    const existingRecord = attendanceData.find(record => record.alunno.id_utente === userId);

    let presenceData = {
      id_lezione: selectedLezione.id_lezione,
      alunno: userId,
      presente: isPresent ? 1 : 0
    };

    try {
      let response;
      if (existingRecord && existingRecord.id_presenza) {
        presenceData.id_presenza = existingRecord.id_presenza;
        response = await updateRegistroPresenze(presenceData);
      } else {
        response = await createRegistroPresenze(presenceData);
      }
      if (response) {
        setAttendanceData(prevData => {
          return prevData.map(record => {
            if (record.alunno.id_utente === userId) {
              return {
                ...record,
                id_presenza: response.id_presenza || record.id_presenza,
                presente: isPresent,
                segnata_da: response.segnata_da || record.segnata_da,
                data_modifica: response.data_modifica || record.data_modifica
              };
            }
            return record;
          });
        });
      }
    } catch (error) {
      console.error("Error updating presence:", error);
    }
  };

  const actions = [
    {
      label: 'Segna Presente',
      icon: 'bi-check-circle',
      onClick: (record) => handlePresenceChange(record.alunno.id_utente, true),
      condition: (record) => record.presente !== true,
    },
    {
      label: 'Segna Assente',
      icon: 'bi-x-circle',
      onClick: (record) => handlePresenceChange(record.alunno.id_utente, false),
      condition: (record) => record.presente !== false,
    },
  ];

  return (
    <div className={`card custom-card w-100 shadow ${isWideScreen ? 'p-5' : 'px-1 py-3'}`}>
      <div className='container'>
        <ComebackArrow path="/gestione-classi" />
        <div className='d-flex justify-content-between custom-card-header mb-3'>
          <div className='d-flex gap-3 custom-card-title my-2'>
            <div className={`custom-icon-container`}>
              {classeData?.corso?.immagine_corso ? 
                <img src={classeData?.corso?.immagine_corso} className='custom-icon' alt='immagine corso'/> : 
                <i className={`bi custom-icon bi-clipboard-check fs-2`}></i>}
            </div>
            <div className='align-self-center'>
              <span className='fs-4 d-block'><i><b>Registro Presenze Lezioni: {classeData?.corso?.denominazione}</b></i></span>
            </div>
          </div>
        </div>
        
        {noDataMessage ? (
          <Alert variant="secondary">{noDataMessage}</Alert>
        ) : (
          <>
            <div className={`d-flex ${isSmallScreen ? 'flex-column' : 'justify-content-between'} align-items-center mb-4`}>
              <Button 
                variant="secondary" 
                onClick={() => handlePrevNextLezione('prev')}
                disabled={lezioni?.indexOf(selectedLezione) === 0 || loadingStatePresenze.isLoadingPresenzeByParams}
                className={`${isSmallScreen ? 'w-100 mb-2' : ''}`}
              >
                <i className="bi bi-chevron-left"></i> Lezione precedente
              </Button>
              <div className={`d-flex align-items-center ${isSmallScreen ? 'w-100 mb-2' : ''}`}>
                <DatePicker
                  selected={selectedDate}
                  onChange={handleDateChange}
                  dateFormat="dd/MM/yyyy"
                  disabled={loadingStatePresenze.isLoadingPresenzeByParams}
                  includeDates={lezioni?.map(l => parseISO(l.data))}
                  customInput={
                    <Button variant="primary" className={`${isSmallScreen ? 'w-100' : ''}`}>
                      {selectedDate ? format(selectedDate, 'EEEE d MMMM yyyy', { locale: it }) : 'Seleziona una data'}
                    </Button>
                  }
                />
              </div>
              <Button 
                variant="secondary" 
                onClick={() => handlePrevNextLezione('next')}
                disabled={lezioni?.indexOf(selectedLezione) === lezioni.length - 1 || loadingStatePresenze.isLoadingPresenzeByParams}
                className={`${isSmallScreen ? 'w-100' : ''}`}
              >
                Lezione successiva <i className="bi bi-chevron-right"></i>
              </Button>
            </div>

            {isInitialLoading || loadingStatePresenze.isLoadingPresenzeByParams ? (
              <LoadingTable rows={3} />
            ) : (
              <>
                {selectedLezione && (
                  <>
                    <div className="mb-2">
                      <h4 className="mb-2">
                        Lezione del {format(parseISO(selectedLezione.data), 'EEEE d MMMM yyyy', { locale: it })}
                      </h4>
                      <div className="d-flex flex-column gap-2">
                        <div>
                          <strong>Titolo:</strong> {selectedLezione.titolo}
                        </div>
                        <div>
                          <strong>Orario:</strong> {selectedLezione.ora_inizio.substring(0, 5)} - {selectedLezione.ora_fine.substring(0, 5)}
                        </div>
                        <div>
                          <strong>Docenti:</strong>{' '}
                          {selectedLezione.docenti?.length > 0 
                            ? selectedLezione.docenti.map(d => `${d.nome} ${d.cognome}`).join(', ')
                            : 'Nessun docente assegnato'
                          }
                        </div>
                      </div>
                    </div>
                    <CustomTable
                      data={attendanceData}
                      columns={columns}
                      actions={actions}
                    />
                  </>
                )}
              </>
            )}
          </>
        )}
      </div>
    </div>
  );
}
